import React from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  MoreHorizontal,
  Pencil,
  Trash2,
  Plus,
  CalendarClock,
} from 'lucide-react';
import { Lead } from '../../../../types';
import { ACTIVITY_ICON_MAP } from '../../../../utils/activityIcons';
import { activitySchedulingStatusLabel } from './leadActivityHelpers';
import { ActivityNotesPanel } from './ActivityNotesPanel';
import { LeadActivityDeleteDialog, type LeadActivityDeleteScope } from './LeadActivityDeleteDialog';
import { LeadNoteHistoryCard } from './LeadNoteHistoryCard';
import {
  ActivityClockedHours,
  ProjectTimeWithoutActivity,
  notifyLeadTimeEntriesChanged,
  useLeadProjectTimeEntries,
  type LeadProjectTimeEntry,
} from './LeadProjectTimeEntries.tsx';
import { TimeEntryIntervalModal } from '../../../../components/timeEntries/TimeEntryIntervalModal';
import { useCapabilities } from '../../../../hooks/useCapabilities';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import type { LeadActivity, NoteHistoryEntry } from '../../../../pages/LeadDetailPage';

type ActivityTypeOption = { id: string; label: string; icon: string };

const ACTIVITY_CARD_CLASS =
  'bg-app-surface border border-app-border rounded-lg overflow-hidden cursor-pointer text-left';

const ACTIVITY_HEADER_CLASS =
  'flex items-start gap-3 p-4 hover:shadow-[inset_0_0_0_1px_#cbd5e1]';

const ACTIVITY_SECTION_LABEL_CLASS = 'text-sm font-medium text-app-text-muted';

const ACTIVITY_MENU_WIDTH = 140;

function ActivityCardMenu({
  open,
  onOpenChange,
  onEdit,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = Math.min(
        Math.max(8, rect.right - ACTIVITY_MENU_WIDTH),
        window.innerWidth - ACTIVITY_MENU_WIDTH - 8,
      );
      const panelH = 88;
      const top = window.innerHeight - rect.bottom < panelH + 8 ? rect.top - 4 - panelH : rect.bottom + 4;
      setPos({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!open);
        }}
        className="rounded p-1.5 text-slate-400 hover:bg-app-hover"
        aria-label="Menü"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              style={{ top: pos.top, left: pos.left, width: ACTIVITY_MENU_WIDTH }}
              className="fixed z-[80] rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onOpenChange(false);
                  onEdit();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
              >
                <Pencil className="h-4 w-4" /> Bearbeiten
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onOpenChange(false);
                  onDelete();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" /> Löschen
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ActivityDoneToggle({
  done,
  onToggle,
}: {
  done: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 hover:border-emerald-500 hover:bg-emerald-50 ${
        done ? 'border-emerald-500 bg-emerald-50' : 'border-app-border-strong'
      }`}
      title={done ? 'Als offen markieren' : 'Als erledigt markieren'}
    >
      {done ? <Check className="h-3 w-3 text-emerald-600" /> : null}
    </button>
  );
}

function activityScheduleLine(activity: LeadActivity, leadName: string, compactDate: boolean): string {
  const status = activitySchedulingStatusLabel(activity);
  if (activity.recurrence_label) {
    const counts =
      activity.occurrence_count != null
        ? ` · ${activity.done_count ?? 0}/${activity.occurrence_count}`
        : '';
    return `${activity.recurrence_label}${counts} · ${leadName} · ${status}`;
  }
  const created = new Date(activity.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  if (!activity.due_at) return `${created} · ${leadName} · ${status}`;
  const d = new Date(activity.due_at);
  const timeStr = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = d.toLocaleDateString(
    'de-DE',
    compactDate
      ? { day: '2-digit', month: 'short' }
      : { weekday: 'long', day: '2-digit', month: 'short' },
  );
  const endStr = activity.end_at
    ? new Date(activity.end_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false })
    : null;
  return `${dateStr} · ${timeStr}${endStr ? ` – ${endStr}` : ''} · ${leadName} · ${status}`;
}

function activityAssigneeNames(activity: LeadActivity): { projectLeads: string; crew: string } {
  const projectLeads = (activity.assignee_users ?? [])
    .map((u) => u.display_name?.trim() || u.email)
    .filter(Boolean)
    .join(', ');
  const crew = (activity.assignee_employees ?? [])
    .map((e) => `${e.first_name} ${e.last_name}`.trim())
    .filter(Boolean)
    .join(', ');
  return { projectLeads, crew };
}

/** Projektleiter / Einsatzteam: zusammengefasst wenn identisch, sonst nebeneinander. */
function ActivityAssigneeLine({ activity }: { activity: LeadActivity }) {
  const { projectLeads, crew } = activityAssigneeNames(activity);
  if (!projectLeads && !crew) return null;

  if (projectLeads && crew && projectLeads === crew) {
    return (
      <p className="text-xs text-app-text-muted mt-1">
        Projektleiter & Einsatzteam: {projectLeads}
      </p>
    );
  }

  return (
    <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-app-text-muted mt-1">
      {projectLeads ? <span>Projektleiter: {projectLeads}</span> : null}
      {crew ? <span>Einsatzteam: {crew}</span> : null}
    </p>
  );
}

/** Angepinnte Notizen + „Geplant“- und „Erledigt“-Aktivitätenlisten (Tab „Zusammenfassung“). */
export function LeadActivityLists({
  lead,
  pinnedNotesForLead,
  noteHistoryMenuEntryId,
  setNoteHistoryMenuEntryId,
  togglePinnedNoteHistory,
  deleteNoteHistoryEntry,
  activities,
  activityTypesList,
  toggleActivityDone,
  activityMenuOpen,
  setActivityMenuOpen,
  fillFormFromActivity,
  deleteActivity,
  checklistNameById,
  resetActivityForm,
  setShowCreateActivityModal,
  children,
}: {
  lead: Lead;
  pinnedNotesForLead: NoteHistoryEntry[];
  noteHistoryMenuEntryId: string | number | null;
  setNoteHistoryMenuEntryId: React.Dispatch<React.SetStateAction<string | number | null>>;
  togglePinnedNoteHistory: (id: string | number) => void | Promise<void>;
  deleteNoteHistoryEntry: (id: string | number) => void | Promise<void>;
  activities: LeadActivity[];
  activityTypesList: ActivityTypeOption[];
  toggleActivityDone: (activityId: string, done: boolean) => void | Promise<void>;
  activityMenuOpen: string | null;
  setActivityMenuOpen: React.Dispatch<React.SetStateAction<string | null>>;
  fillFormFromActivity: (activity: LeadActivity) => void;
  deleteActivity: (activityId: string, scope?: LeadActivityDeleteScope) => Promise<string | null>;
  checklistNameById: Map<string, string>;
  resetActivityForm: () => void;
  setShowCreateActivityModal: React.Dispatch<React.SetStateAction<boolean>>;
  children?: React.ReactNode;
}) {
  const { items: timeEntries, activities: timeActivities } = useLeadProjectTimeEntries(String(lead.id));
  const { can } = useCapabilities();
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  // Ist-Zeiten gehören zur Zeiterfassung (Modul Mitarbeiter).
  const canEditIst = can('web.projekte.edit') && modulesLoaded && isEnabled('hr');
  // From an appointment card the Ist time is locked to that appointment (`activityId`);
  // project time without appointment is edited unlocked (new ones are added from Controlling).
  const [istDialog, setIstDialog] = React.useState<{
    entry: LeadProjectTimeEntry | null;
    activityId: string | null;
  } | null>(null);
  const [istNotice, setIstNotice] = React.useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<LeadActivity | null>(null);
  const editIst = canEditIst
    ? (row: LeadProjectTimeEntry) => setIstDialog({ entry: row, activityId: row.activity_id })
    : undefined;
  const addIstFor = (activityId: string) =>
    canEditIst ? () => setIstDialog({ entry: null, activityId }) : undefined;

  return (
    <>
      {pinnedNotesForLead.length > 0 ? (
        <div className="pt-1 pb-2 space-y-3 shrink-0">
          <h3 className={ACTIVITY_SECTION_LABEL_CLASS}>Angepinnt</h3>
          {pinnedNotesForLead.map((pn) => {
            const pinnedMenuOpen = noteHistoryMenuEntryId === pn.id;
            const pinnedCount = (lead.pinned_note_history_ids ?? []).length;
            return (
              <LeadNoteHistoryCard
                key={`pinned-note-${pn.id}`}
                title="Angepinnte Notiz"
                iconVariant="pin"
                snippet={pn.snippet || ''}
                savedAt={pn.saved_at}
                authorLabel={pn.author_label}
                isPinned
                pinnedCount={pinnedCount}
                menuOpen={pinnedMenuOpen}
                onToggleMenu={() =>
                  setNoteHistoryMenuEntryId((prev) => (prev === pn.id ? null : pn.id))
                }
                onTogglePin={() => void togglePinnedNoteHistory(pn.id)}
                onDelete={() => void deleteNoteHistoryEntry(pn.id)}
              />
            );
          })}
        </div>
      ) : null}

      <div className="min-w-0">
        {/*
          Leeren „Geplant“-Block ausblenden, wenn oben schon angepinnte Notizen stehen
          (Aktivität planen weiter z. B. per FAB / Modal).
        */}
        {(() => {
          const openActivities = activities.filter((a) => !a.done);
          const hideGeplantWhenEmptyWithPins =
            pinnedNotesForLead.length > 0 && openActivities.length === 0;
          if (hideGeplantWhenEmptyWithPins) return null;
          return (
            <>
              <div className="flex flex-col items-start my-6">
                <h3 className={ACTIVITY_SECTION_LABEL_CLASS}>Geplant</h3>
              </div>
              <div className="w-full mb-2 space-y-2">
                {openActivities.length === 0 ? (
                  <div className="py-8 px-6 text-center bg-app-surface border border-app-border rounded-lg">
                    <p className="text-sm text-app-text-subtle mb-4">Keine geplanten Aktivitäten.</p>
                    <button
                      type="button"
                      onClick={() => {
                        resetActivityForm();
                        setShowCreateActivityModal(true);
                      }}
                      className="inline-flex items-center gap-2 text-sm font-medium text-pipedrive-blue hover:text-blue-700 hover:underline"
                    >
                      <Plus className="w-4 h-4" />
                      Eine Aktivität planen
                    </button>
                  </div>
                ) : (
                  openActivities.map((activity) => (
            <div
              key={activity.id}
              role="button"
              tabIndex={0}
              onClick={() => fillFormFromActivity(activity)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fillFormFromActivity(activity);
                }
              }}
              className={`${ACTIVITY_CARD_CLASS} w-full`}
              aria-label={`${activity.title} bearbeiten`}
            >
            <div className={ACTIVITY_HEADER_CLASS}>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-app-surface-muted">
                {(function () {
                  const typeEntry = activityTypesList.find((t) => t.id === activity.activity_type);
                  const Icon = typeEntry ? (ACTIVITY_ICON_MAP[typeEntry.icon] ?? CalendarClock) : CalendarClock;
                  return <Icon className="h-4 w-4 text-app-text-muted" />;
                })()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-app-text">{activity.title}</p>
                <p className="text-xs text-app-text-subtle mt-0.5">
                  {activityScheduleLine(activity, lead.name, false)}
                </p>
                <ActivityAssigneeLine activity={activity} />
                <ActivityClockedHours
                  activityId={activity.id}
                  occurrenceIds={activity.occurrence_ids}
                  items={timeEntries}
                  onEdit={editIst}
                  onAdd={addIstFor(activity.id)}
                />
                {activity.checklist_id && checklistNameById.get(activity.checklist_id) ? (
                  <p className="text-xs text-app-text-muted mt-0.5">
                    Checkliste: {checklistNameById.get(activity.checklist_id)}
                  </p>
                ) : null}
              </div>
              <ActivityDoneToggle done={false} onToggle={() => void toggleActivityDone(activity.id, true)} />
              <ActivityCardMenu
                open={activityMenuOpen === activity.id}
                onOpenChange={(next) => setActivityMenuOpen(next ? activity.id : null)}
                onEdit={() => fillFormFromActivity(activity)}
                onDelete={() => setPendingDelete(activity)}
              />
            </div>
            <ActivityNotesPanel notes={activity.notes} />
            </div>
                  ))
                )}
              </div>
            </>
          );
        })()}

        {/* Erledigt: nur anzeigen wenn es erledigte Aktivitäten gibt */}
        {activities.some((a) => a.done) && (
          <div className="flex flex-col items-start my-6">
            <h3 className={ACTIVITY_SECTION_LABEL_CLASS}>Erledigt</h3>
          </div>
        )}
        {activities.filter((a) => a.done).length > 0 && (
          <div className="w-full mb-4 space-y-2">
            {activities.filter((a) => a.done).map((activity) => (
              <div
                key={activity.id}
                role="button"
                tabIndex={0}
                onClick={() => fillFormFromActivity(activity)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fillFormFromActivity(activity);
                  }
                }}
                className={`${ACTIVITY_CARD_CLASS} opacity-90`}
                aria-label={`${activity.title} bearbeiten`}
              >
              <div className={ACTIVITY_HEADER_CLASS}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  {(function () {
                    const typeEntry = activityTypesList.find((t) => t.id === activity.activity_type);
                    const Icon = typeEntry ? (ACTIVITY_ICON_MAP[typeEntry.icon] ?? CalendarClock) : CalendarClock;
                    return <Icon className="h-4 w-4 text-emerald-600" />;
                  })()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-app-text-muted line-through">{activity.title}</p>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {activityScheduleLine(activity, lead.name, true)}
                  </p>
                  <ActivityAssigneeLine activity={activity} />
                  <ActivityClockedHours
                    activityId={activity.id}
                    occurrenceIds={activity.occurrence_ids}
                    items={timeEntries}
                    onEdit={editIst}
                    onAdd={addIstFor(activity.id)}
                  />
                </div>
                <ActivityDoneToggle done onToggle={() => void toggleActivityDone(activity.id, false)} />
                <ActivityCardMenu
                  open={activityMenuOpen === activity.id}
                  onOpenChange={(next) => setActivityMenuOpen(next ? activity.id : null)}
                  onEdit={() => fillFormFromActivity(activity)}
                  onDelete={() => setPendingDelete(activity)}
                />
              </div>
              <ActivityNotesPanel notes={activity.notes} />
              </div>
            ))}
          </div>
        )}

        <ProjectTimeWithoutActivity
          items={timeEntries}
          onEdit={editIst}
          sectionLabelClassName={ACTIVITY_SECTION_LABEL_CLASS}
        />
        {istNotice ? (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">{istNotice}</div>
        ) : null}

        {children}
      </div>
      {pendingDelete ? (
        <LeadActivityDeleteDialog
          activity={pendingDelete}
          onDelete={(scope) => deleteActivity(pendingDelete.id, scope)}
          onClose={() => setPendingDelete(null)}
        />
      ) : null}
      <TimeEntryIntervalModal
        open={istDialog !== null}
        onClose={() => setIstDialog(null)}
        onSaved={(message) => {
          setIstDialog(null);
          setIstNotice(message ?? null);
          notifyLeadTimeEntriesChanged(String(lead.id));
        }}
        scope={{ kind: 'lead', leadId: String(lead.id), activities: timeActivities }}
        entry={istDialog?.entry ?? null}
        lockedActivityId={istDialog?.activityId ?? null}
      />
    </>
  );
}
